import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

import { isNativePlatform } from "./platform";

export type LocationCaptureSource = "capacitor" | "browser";

export interface CapturedLocation {
  latitude: number;
  longitude: number;
  accuracyMeters?: number | null;
  timestamp: Date;
  source: LocationCaptureSource;
}

interface CaptureOptions {
  /** Milliseconds before we stop waiting for a location fix */
  timeoutMs?: number;
  /** Whether to request high-accuracy data when using browser APIs */
  enableHighAccuracy?: boolean;
}

const DEFAULT_TIMEOUT = 12_000;

const isGeolocationPluginAvailable = (): boolean => {
  try {
    return Capacitor.isPluginAvailable("Geolocation");
  } catch {
    return false;
  }
};

const captureViaCapacitor = async (
  options: CaptureOptions
): Promise<CapturedLocation | null> => {
  if (!isNativePlatform()) return null;
  if (!isGeolocationPluginAvailable()) return null;

  try {
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: options.timeoutMs ?? DEFAULT_TIMEOUT,
    });
    const { latitude, longitude, accuracy } = position.coords;
    if (
      typeof latitude !== "number" ||
      Number.isNaN(latitude) ||
      typeof longitude !== "number" ||
      Number.isNaN(longitude)
    ) {
      return null;
    }
    return {
      latitude,
      longitude,
      accuracyMeters:
        typeof accuracy === "number" && !Number.isNaN(accuracy)
          ? accuracy
          : undefined,
      timestamp: new Date(position.timestamp),
      source: "capacitor",
    };
  } catch (error) {
    console.warn("[location] capacitor geolocation failed", error);
    return null;
  }
};

const captureViaBrowser = async (
  options: CaptureOptions
): Promise<CapturedLocation | null> => {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return null;
  }

  return new Promise<CapturedLocation | null>((resolve) => {
    const onSuccess: PositionCallback = (pos) => {
      const { latitude, longitude, accuracy } = pos.coords;
      if (
        typeof latitude !== "number" ||
        Number.isNaN(latitude) ||
        typeof longitude !== "number" ||
        Number.isNaN(longitude)
      ) {
        resolve(null);
        return;
      }
      resolve({
        latitude,
        longitude,
        accuracyMeters:
          typeof accuracy === "number" && !Number.isNaN(accuracy)
            ? accuracy
            : undefined,
        timestamp: new Date(pos.timestamp),
        source: "browser",
      });
    };

    const onError: PositionErrorCallback = (error) => {
      console.warn("[location] browser geolocation failed", error);
      resolve(null);
    };

    try {
      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        maximumAge: 0,
        timeout: options.timeoutMs ?? DEFAULT_TIMEOUT,
      });
    } catch (error) {
      console.warn("[location] navigator.geolocation call failed", error);
      resolve(null);
    }
  });
};

export const captureCurrentLocation = async (
  options: CaptureOptions = {}
): Promise<CapturedLocation | null> => {
  const fromCapacitor = await captureViaCapacitor(options);
  if (fromCapacitor) {
    console.debug("[location] captured via capacitor", fromCapacitor);
    return fromCapacitor;
  }

  const fromBrowser = await captureViaBrowser(options);
  if (fromBrowser) {
    console.debug("[location] captured via browser", fromBrowser);
    return fromBrowser;
  }

  console.debug("[location] no geolocation available");
  return null;
};
